import * as React from "react";
import { Input, PhoneInput, SearchableSelect, useToast } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { BIN_LENGTH, isRequiredBinValid, validateBin } from "@racoon/lib";
import {
  useCreateServiceReceiverMutation,
  useUpdateOrganizationMutation,
  useListCounterpartiesQuery,
} from "~/shared/api/organization/organization.grpc";
import {
  useCreateContactMutation,
  useUpdateContactMutation,
} from "~/shared/api/contact/contact.grpc";
import { OnboardingField, Section } from "./onboarding-layout";
import { useCustomerBinDuplicate } from "~/shared/hooks/use-bin-duplicate";
import { DISABLED_INPUT_CLASS } from "~/shared/ui/disabled-field";
import { ModeTabs } from "~/shared/ui/mode-tabs";
import { StepShell } from "./step-shell";
import { LabelSelect } from "~/shared/ui/label.select";
import { useCustomerTypeOptions, useSetCustomerType } from "~/shared/hooks/use-customer-types";

type CustomerMode = "new" | "existing";

/** Breathing room between creating the organization and the contact that points at it. */
const CONTACT_CREATE_DELAY_MS = 250;

const BIN_MESSAGE_ID = "onboarding-customer-bin-message";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type Props = {
  providerId?: number;
  organizationId?: bigint;
  /** Whether the form holds input that was never saved — the wizard confirms closing over it. */
  onDirtyChange: (dirty: boolean) => void;
  onCreated: (organizationId: bigint, customerName: string) => void;
  /** Picking one that already exists opens the whole wizard up — see `isStepUnlocked`. */
  onPickedExisting: (organization: ConnectProto.Organization) => void;
};

/**
 * Step 1 — the customer: created here with its primary contact (org → contact →
 * primaryContactId), or picked from the provider's existing ones. The type is
 * always SERVICE_RECEIVER and bank details have no RPC, so neither is a field.
 */
export const CustomerStep: React.FC<Props> = ({
  providerId,
  organizationId,
  onDirtyChange,
  onCreated,
  onPickedExisting,
}) => {
  const t = useT();
  const { toast } = useToast();

  const [mode, setMode] = React.useState<CustomerMode>("new");
  const [name, setName] = React.useState("");
  // The provider's type for the new customer, saved once it exists ("0": none).
  const [customerTypeId, setCustomerTypeId] = React.useState("0");
  const customerTypeOptions = useCustomerTypeOptions(providerId, customerTypeId);
  const saveCustomerType = useSetCustomerType();
  const [bin, setBin] = React.useState("");
  const [jurAddress, setJurAddress] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [contactPosition, setContactPosition] = React.useState("");
  const [contactPhone, setContactPhone] = React.useState("");
  const [contactId, setContactId] = React.useState<bigint | undefined>();
  const [primaryLinked, setPrimaryLinked] = React.useState(false);
  const [selectedExistingId, setSelectedExistingId] = React.useState("");
  /** The customer the duplicate error sent us to, so the pick survives the list still loading. */
  const [pickedFromDuplicate, setPickedFromDuplicate] = React.useState<ConnectProto.Organization>();

  const [createOrganization, { isLoading: creating }] = useCreateServiceReceiverMutation();
  const [updateOrganization, { isLoading: updating }] = useUpdateOrganizationMutation();
  const [createContact, { isLoading: creatingContact }] = useCreateContactMutation();
  const [updateContact, { isLoading: updatingContact }] = useUpdateContactMutation();
  // Only a customer created by this form may be updated from it; a picked one is just pointed at.
  // Held as the id so that picking another customer afterwards withdraws the right to update.
  const [createdId, setCreatedId] = React.useState<bigint>();

  const { data: counterparties, isFetching: loadingCounterparties } = useListCounterpartiesQuery(
    { organizationId: BigInt(providerId ?? 0) },
    { skip: !providerId || mode !== "existing" },
  );
  // ListCounterparties answers with counterparty rows, each wrapping its organization.
  const existingCustomers = React.useMemo<ConnectProto.Organization[]>(
    () =>
      (counterparties?.counterparties ?? []).flatMap((c) =>
        c.organization ? [c.organization] : [],
      ),
    [counterparties],
  );

  const isExisting = mode === "existing";
  const isSaved = organizationId != null;
  const createdHere = isSaved && organizationId === createdId;
  const isBusy = creating || updating || creatingContact || updatingContact;
  const hasContact = Boolean(contactName.trim() || contactPosition.trim() || contactPhone.trim());

  const trimmedBin = bin.trim();
  const isDirty = !isSaved && Boolean(name.trim() || trimmedBin || jurAddress.trim() || hasContact);
  React.useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);
  const binIssue = validateBin(bin);

  const {
    duplicate,
    archived,
    pending: binCheckPending,
    failed: binCheckFailed,
  } = useCustomerBinDuplicate({
    providerId,
    bin,
    excludeOrgId: organizationId ?? undefined,
    skip: isExisting,
  });

  const customerName = React.useCallback(
    (org: ConnectProto.Organization) => org.name?.trim() || t(Namespace.Common, "unnamed"),
    [t],
  );

  /** Name plus BIN in one string, for the trigger while the pick is not in the list yet. */
  const customerLabel = React.useCallback(
    (org: ConnectProto.Organization) =>
      [customerName(org), org.bin?.trim()].filter(Boolean).join(" · "),
    [customerName],
  );

  // Name on the left, BIN muted on the right; the search matches either.
  const customerOptions = React.useMemo(
    () =>
      existingCustomers.map((org) => ({
        id: String(org.id),
        name: customerName(org),
        secondary: org.bin?.trim() || undefined,
      })),
    [existingCustomers, customerName],
  );

  // Read-only echo of the picked customer, so the pick can be confirmed.
  const selectedExisting = React.useMemo(
    () =>
      existingCustomers.find((org) => String(org.id) === selectedExistingId) ??
      (pickedFromDuplicate && String(pickedFromDuplicate.id) === selectedExistingId
        ? pickedFromDuplicate
        : undefined),
    [existingCustomers, selectedExistingId, pickedFromDuplicate],
  );

  /** The duplicate error's way out: switch to the existing-customer tab with it already picked. */
  const handleUseDuplicate = () => {
    if (!duplicate) return;
    setPickedFromDuplicate(duplicate);
    setSelectedExistingId(String(duplicate.id));
    setMode("existing");
  };

  /** Points the organization at its contact — the second half of the write, retried on its own. */
  const linkPrimaryContact = async (orgId: bigint, primaryContactId: bigint) => {
    await updateOrganization({ id: orgId, primaryContactId }).unwrap();
    setPrimaryLinked(true);
  };

  /** Writes the contact for `orgId`, creating it the first time and updating it after. */
  const saveContact = async (orgId: bigint) => {
    if (!hasContact) return;
    if (contactId != null) {
      await updateContact({
        id: contactId,
        // ownerOrganizationId scopes the contact; the update needs it too.
        ownerOrganizationId: orgId,
        name: contactName.trim(),
        position: contactPosition.trim(),
        phone: contactPhone,
      }).unwrap();
      // A create that landed before the link failed leaves the org without a primary
      // contact; "Обновить" is the retry, so it has to redo the link as well.
      if (!primaryLinked) await linkPrimaryContact(orgId, contactId);
      return;
    }
    // A contact created right after its org sometimes loses the race on the backend.
    await delay(CONTACT_CREATE_DELAY_MS);

    // int64 ids arrive as decimal strings: parse straight to bigint, Number rounds past 2^53.
    const created = await createContact({
      ownerOrganizationId: orgId,
      name: contactName.trim(),
      position: contactPosition.trim(),
      phone: contactPhone,
    }).unwrap();
    setContactId(BigInt(created.id));
    await linkPrimaryContact(orgId, BigInt(created.id));
  };

  const handleCreate = async () => {
    if (!providerId || duplicate) return;
    try {
      const saved = await createOrganization({
        serviceProviderId: BigInt(providerId),
        organization: {
          name: name.trim(),
          type: ConnectProto.OrganizationType.SERVICE_RECEIVER,
          bin: bin.trim() || undefined,
          jurAddress: jurAddress.trim() || undefined,
        },
      }).unwrap();

      // The id is reported first, so a failed contact write is retried via
      // "Обновить" against this customer instead of creating a second one.
      const newId = BigInt(saved.id);
      setCreatedId(newId);
      onCreated(newId, name.trim());
      if (customerTypeId !== "0") {
        try {
          await saveCustomerType(providerId, newId, customerTypeId);
        } catch {
          // GlobalErrorToaster shows the API error; the customer itself is created.
        }
      }
      try {
        await saveContact(newId);
      } catch (error) {
        console.error("Failed to save the customer's contact", error);
        toast({
          variant: "destructive",
          title: t(Namespace.Customers, "onboarding.customer.contactFailed"),
        });
      }
    } catch (error) {
      console.error("Failed to create customer", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.customer.createFailed"),
      });
    }
  };

  const handleUpdate = async () => {
    if (organizationId == null || !createdHere || duplicate) return;
    try {
      // "" clears an explicit-presence field; undefined would keep the old value.
      await updateOrganization({
        id: organizationId,
        name: name.trim(),
        bin: bin.trim(),
        jurAddress: jurAddress.trim(),
      }).unwrap();
      await saveContact(organizationId);
      toast({ title: t(Namespace.Customers, "onboarding.customer.updated") });
    } catch (error) {
      console.error("Failed to update customer", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.customer.updateFailed"),
      });
    }
  };

  const duplicateName = duplicate?.name?.trim() || t(Namespace.Common, "unnamed");

  // One line under the BIN: the format complaint, the duplicate and its way out, a failed
  // check, or the check still running. An existing customer's BIN is read-only, so it says nothing.
  const binMessage = isExisting ? null : binIssue ? (
    <p id={BIN_MESSAGE_ID} className="pt-1 text-xs text-text-error">
      {t(Namespace.Validation, binIssue.key, binIssue.vars)}
    </p>
  ) : duplicate ? (
    <p id={BIN_MESSAGE_ID} className="pt-1 text-xs text-text-error">
      {archived
        ? t(Namespace.Customers, "card.binTakenArchived", { name: duplicateName })
        : t(Namespace.Customers, "onboarding.customer.binTaken", { name: duplicateName })}{" "}
      <button
        type="button"
        onClick={handleUseDuplicate}
        className="font-medium text-blue-600 underline hover:text-blue-700 focus:outline-none"
      >
        {t(Namespace.Customers, "onboarding.customer.binTakenPick", { name: duplicateName })}
      </button>
    </p>
  ) : binCheckFailed ? (
    <p id={BIN_MESSAGE_ID} className="pt-1 text-xs text-text-error">
      {t(Namespace.Customers, "card.binCheckFailed")}
    </p>
  ) : binCheckPending ? (
    <p id={BIN_MESSAGE_ID} className="pt-1 text-xs text-icon-secondary">
      {t(Namespace.Customers, "onboarding.customer.binChecking")}
    </p>
  ) : null;
  const binMessageId = binMessage ? BIN_MESSAGE_ID : undefined;

  const primary = isExisting
    ? {
        label: t(Namespace.Customers, "onboarding.customer.pick"),
        onClick: () => selectedExisting && onPickedExisting(selectedExisting),
        disabled: !selectedExisting,
      }
    : {
        label: t(Namespace.Customers, "onboarding.customer.create"),
        onClick: handleCreate,
        disabled:
          isSaved ||
          isBusy ||
          !name.trim() ||
          !providerId ||
          !isRequiredBinValid(bin) ||
          binCheckPending ||
          binCheckFailed ||
          duplicate != null,
      };

  return (
    <StepShell
      primary={primary}
      // An existing customer is only pointed at, never updated here.
      secondary={
        isSaved && createdHere && !isExisting
          ? {
              label: t(Namespace.Customers, "onboarding.customer.update"),
              onClick: handleUpdate,
              disabled:
                isBusy ||
                !name.trim() ||
                !isRequiredBinValid(bin) ||
                binCheckPending ||
                binCheckFailed ||
                duplicate != null,
            }
          : undefined
      }
    >
      <div className="flex w-full flex-col gap-8">
        <ModeTabs
          value={mode}
          onChange={setMode}
          options={[
            { value: "new", label: t(Namespace.Customers, "onboarding.customer.tabNew") },
            { value: "existing", label: t(Namespace.Customers, "onboarding.customer.tabExisting") },
          ]}
        />

        <Section title={t(Namespace.Customers, "onboarding.customer.legalSection")}>
          {isExisting ? (
            <OnboardingField
              label={t(Namespace.Customers, "onboarding.customer.pickLabel")}
              htmlFor="onboarding-existing-customer"
            >
              <SearchableSelect
                id="onboarding-existing-customer"
                value={selectedExistingId}
                onChange={setSelectedExistingId}
                options={customerOptions}
                // Carried over from the duplicate error, the pick is not in the list yet.
                valueLabel={selectedExisting && customerLabel(selectedExisting)}
                loading={loadingCounterparties}
                placeholder={t(Namespace.Customers, "onboarding.selectPlaceholder")}
                searchPlaceholder={t(Namespace.Customers, "searchPlaceholder")}
              />
            </OnboardingField>
          ) : (
            <OnboardingField
              label={t(Namespace.Customers, "card.legalName.label")}
              htmlFor="onboarding-customer-name"
              required
            >
              <Input
                id="onboarding-customer-name"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder={t(Namespace.Customers, "card.legalName.placeholder")}
              />
            </OnboardingField>
          )}

          <OnboardingField
            label={t(Namespace.Customers, "card.bin.label")}
            htmlFor="onboarding-customer-bin"
            alignTop={!isExisting}
            required={!isExisting}
          >
            <div>
              <Input
                id="onboarding-customer-bin"
                value={isExisting ? (selectedExisting?.bin ?? "") : bin}
                disabled={isExisting}
                inputMode="numeric"
                maxLength={BIN_LENGTH}
                aria-invalid={!isExisting && (!!binIssue || duplicate != null)}
                aria-describedby={binMessageId}
                className={DISABLED_INPUT_CLASS}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBin(e.target.value)}
                placeholder={isExisting ? "" : t(Namespace.Customers, "card.bin.placeholder")}
              />
              {binMessage}
            </div>
          </OnboardingField>

          {!isExisting && customerTypeOptions.length > 0 && (
            <OnboardingField
              label={t(Namespace.Customers, "card.customerType.label")}
              htmlFor="onboarding-customer-type"
            >
              <LabelSelect
                id="onboarding-customer-type"
                options={customerTypeOptions}
                value={customerTypeId}
                onChange={setCustomerTypeId}
                disabled={createdHere}
              />
            </OnboardingField>
          )}

          <OnboardingField
            label={t(Namespace.Customers, "card.jur_address.label")}
            htmlFor="onboarding-customer-jur-address"
          >
            <Input
              id="onboarding-customer-jur-address"
              value={isExisting ? (selectedExisting?.jurAddress ?? "") : jurAddress}
              disabled={isExisting}
              className={DISABLED_INPUT_CLASS}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJurAddress(e.target.value)}
              placeholder={isExisting ? "" : t(Namespace.Customers, "card.jur_address.placeholder")}
            />
          </OnboardingField>
        </Section>

        <Section title={t(Namespace.Customers, "onboarding.customer.contactSection")}>
          <OnboardingField
            label={t(Namespace.Customers, "onboarding.customer.contactName")}
            htmlFor="onboarding-contact-name"
          >
            <Input
              id="onboarding-contact-name"
              value={isExisting ? (selectedExisting?.primaryContact?.name ?? "") : contactName}
              disabled={isExisting}
              className={DISABLED_INPUT_CLASS}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactName(e.target.value)}
            />
          </OnboardingField>

          <OnboardingField
            label={t(Namespace.Customers, "onboarding.customer.contactPosition")}
            htmlFor="onboarding-contact-position"
          >
            <Input
              id="onboarding-contact-position"
              value={
                isExisting ? (selectedExisting?.primaryContact?.position ?? "") : contactPosition
              }
              disabled={isExisting}
              className={DISABLED_INPUT_CLASS}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setContactPosition(e.target.value)
              }
            />
          </OnboardingField>

          <OnboardingField
            label={t(Namespace.Customers, "onboarding.customer.contactPhone")}
            htmlFor="onboarding-contact-phone"
          >
            {/* PhoneInput's country select has nothing to show read-only; a plain input instead. */}
            {isExisting ? (
              <Input
                id="onboarding-contact-phone"
                value={selectedExisting?.primaryContact?.phone ?? ""}
                disabled
                readOnly
                className={DISABLED_INPUT_CLASS}
              />
            ) : (
              <PhoneInput
                id="onboarding-contact-phone"
                value={contactPhone}
                onValueChange={setContactPhone}
                defaultCountry="KZ"
                allowedCountries={["KZ", "RU", "US"]}
                wrapperClassName="w-full"
              />
            )}
          </OnboardingField>
        </Section>
      </div>
    </StepShell>
  );
};
